/**
 * The module ribbon is ONE working bar: the property / period / version chips
 * on the left, and whatever the active page needs on the right. Pages don't
 * know about the ribbon; they render their toolbar as usual, and when a slot
 * node is available they portal it there instead of drawing a second bar
 * under the header. One component, two hosts — the classic shell shows the
 * same ribbon with the tab strip beneath it, the inset shell moves the tabs
 * into the breadcrumb.
 */

import React, { createContext, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

interface RibbonSlotValue {
  node: HTMLElement | null;
  setNode: (node: HTMLElement | null) => void;
}

const RibbonSlotContext = createContext<RibbonSlotValue>({ node: null, setNode: () => {} });

export function RibbonSlotProvider({ children }: { children: React.ReactNode }) {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const value = useMemo(() => ({ node, setNode }), [node]);
  return <RibbonSlotContext.Provider value={value}>{children}</RibbonSlotContext.Provider>;
}

/** The ribbon's right-hand target; `ref` it onto the slot element. */
export function useRibbonSlotTarget() {
  return useContext(RibbonSlotContext).setNode;
}

/**
 * Render `children` into the ribbon when a slot exists, otherwise inline via
 * `fallback` (a page rendered outside the module frame, e.g. a pop-out).
 */
export function RibbonPortal({ children, fallback }: { children: React.ReactNode; fallback?: React.ReactNode }) {
  const { node } = useContext(RibbonSlotContext);
  if (!node) return <>{fallback ?? null}</>;
  return createPortal(children, node);
}

export function useHasRibbonSlot() {
  return !!useContext(RibbonSlotContext).node;
}
